import React, { useEffect, useState } from 'react'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@material-ui/core/Box'
import { Typography } from '@material-ui/core'
import cn from 'classnames'
import { getCartTotals, getInitializedValues, getTips } from '../Selectors'
import { VNButton } from 'vn-react-components/VNComponents/VNButton'
import { setCFDScreen, setCartTotals } from '../ActionCreators'
import { CFD_SCREEN } from '../Reducer'
import { VNDialog } from 'vn-react-components/VNComponents/VNDialog'
import { centsToDollars } from '../../utils/formatters'
import { Info } from '@material-ui/icons'
import { VNProcessing } from './VNProcessing'
import { VNButtonContainer } from '../components/VNButtonContainer'
import { useVnPayment } from '../hooks/useProcessPayment'
import { setConfirmDefaultTipModal, setVNTipEntryDialog } from '../../VNDialogs/ActionCreators'
import { getNextDollar } from '../../utils/charityConfig'
import { isEmpty } from 'lodash'
// for debugging
// import { Map } from 'immutable'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  list: {
    width: 416,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  totalsContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'scroll',
  },
  containerButton: {
    border: '2px solid #ffffff',
    borderRadius: 8,
    width: '100%',
  },
  totals: {
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    flex: 1,
    overflowY: 'auto',
  },
  serviceChargeTotal: {
    display: 'flex',
    alignItems: 'center',
  },
  total: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  discount: {
    color: `${theme.custom.colors.brand} !important`,
  },
  bottomPayButton: {
    paddingBottom: 60,
  },
  tipText: {
    color: `${theme.custom.colors.disabledGrey} !important`,
    textAlign: 'center',
    paddingTop: 43,
    paddingBottom: 32,
  },
  payButton: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.custom.colors.black,
    width: 416,
  },
}))

export const VNPayment = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()

  // SELECTORS
  const cartTotals = useSelector((state) => getCartTotals(state))
  const initialConfiguration = useSelector((state) => getInitializedValues(state))
  const charitySettings = initialConfiguration?.get('charitySettings')
  const confirmDefaultTipEnabled = initialConfiguration?.get('defaultTipEnabled')

  // cartTotals below used for debug
  // const cartTotals = Map({
  //   "subtotal": 1234,
  //   "discount": 15,
  //   "tax": 86,
  //   "tip": "0",
  //   "total": 1320,
  //   "uuid": "81fd39bb-5ee3-4a30-9287-370b03b21fc2",
  //   "serviceCharge": 220,
  //   "serviceChargeName": "Service Charge (POS) super long name that should be truncated",
  //   "orderNumber": "869230",
  //   "hRef": "1422869230"
  // })
  const tips = useSelector((state) => getTips(state))
  // set to true in the case that there is no default tip.
  const [defaultTipChanged, setDefaultTipChanged] = useState(true)
  const [defaultTipPercentage, setDefaultTipPercentage] = useState(0)
  // tips below used for debug
  // const tips = Map({enabled: 'true', suggestions: [{tipCustom: 'true', tipSelected: 'false'}, {tipDisplay: 'No Tip', tipFree: 'true', tipCustom: 'false', tipValue: 0, tipSelected: 'false'}]})

  // LOCAL STATE
  const [loaded, setLoaded] = useState(false)
  const [serviceChargeDialog, setServiceChargeDialog] = useState(false)
  const [lastTipButtonSelected, setLastTipButtonSelected] = useState(null)

  const { handlePaymentClick, getTotalInCents } = useVnPayment()

  // USE EFFECTS

  useEffect(() => {
    let totals = cartTotals.toJS()

    if (tips && tips.get('enabled') === 'true' && tips.get('suggestions').length > 0) {
      let defaultTipPercentage = 0

      // if the total is 0, we don't want to use the default tip suggestion
      const totalInCents = getTotalWithoutTipInCents()
      if (totalInCents === 0) {
        return
      }

      for (let i = 0; i < tips.get('suggestions').length; i++) {
        const element = tips.get('suggestions')[i]
        if (element.tipSelected === 'true') {
          setDefaultTipChanged(false)
          setDefaultTipPercentage(element?.tipDisplay)
          if (element.tipAmountInCents) {
            totals.tip = parseInt(element.tipAmountInCents)
          } else {
            defaultTipPercentage = parseFloat(element.tipPercentage)
            if (defaultTipPercentage > 0) {
              totals.tip = parseInt(defaultTipPercentage * totals.subtotal)
            }
          }
        }
      }
    }

    dispatch(setCartTotals(totals))

    return () => {
      dispatch(
        setConfirmDefaultTipModal({
          open: false,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loaded) return

    if (tips && tips.get('enabled') === 'true') {
      setLoaded(true)
      return
    }

    if (cartTotals?.get('serviceCharge') && parseInt(cartTotals.get('serviceCharge')) !== 0) {
      setLoaded(true)
      return
    }

    setLoaded(true)
    onPay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tips, cartTotals])

  const onButtonContainerClick = (button) => {
    const id = button?.id
    // force it so you can't click the same button twice,
    // saving time on the client for more responsiveness
    if (id !== lastTipButtonSelected) {
      setDefaultTipChanged(true)
      let percent
      let tip

      setLastTipButtonSelected(id)

      switch (id) {
        case 'custom':
          dispatch(setVNTipEntryDialog(true, onDialogSave, 0, 'Custom Tip', false, 'Save', true))
          break
        case 'none':
          percent = 0
          break
        default:
          percent = id
          break
      }

      const tipAmountInCents = tips.get('suggestions')?.reduce((tipAmount, tipSuggestion) => {
        if (tipSuggestion.tipDisplay === id) {
          return tipSuggestion.tipAmountInCents
        }
        return tipAmount
      }, null)

      if (tipAmountInCents) {
        // if a tipAmountInCents value exists, this means we are online with a successful totals request
        // that has already provided a designated tip amount for the displayed percentage value
        tip = parseInt(tipAmountInCents)
      } else if (percent) {
        // tip is calculated off the subtotal before tax and discounts
        tip = parseInt(cartTotals.get('subtotal') * percent)
      } else if (percent === 0) {
        tip = 0
      } else {
        // waiting on the custom tip
        return
      }

      let totals = cartTotals.toJS()
      totals.tip = tip

      dispatch(setCartTotals(totals))
    }
  }

  // calculate the tip and save it
  const onDialogSave = (val) => {
    if (val === undefined || val === null) {
      return
    }

    let totals = cartTotals.toJS()
    totals.tip = val
    dispatch(setVNTipEntryDialog(false))
    dispatch(setCartTotals(totals))
  }

  const getTotalWithoutTipInCents = () => {
    return parseInt(getTotalInCents()) - cartTotals?.get('tip')
  }

  const shouldNavigateToCharity = () => {
    const isNextDollarZero =
      charitySettings?.roundUpForCharityEnabled === 'true' && getNextDollar(parseInt(getTotalInCents())) === 0

    if (charitySettings?.roundUpForCharityEnabled !== 'true') {
      return false
    }

    if (isNextDollarZero && charitySettings?.charityOptionsEnabled !== 'true') {
      return false
    }

    if (isNextDollarZero && charitySettings?.charityOptionsEnabled === 'true' && isEmpty(charitySettings?.charityOptions)) {
      return false
    }

    return charitySettings?.roundUpForCharityEnabled === 'true' || charitySettings?.charityOptionsEnabled === 'true'
  }

  const onPay = (_, tipConfirmed = false) => {
    if (!defaultTipChanged && !tipConfirmed && confirmDefaultTipEnabled === 'true') {
      dispatch(
        setConfirmDefaultTipModal({
          open: true,
          onConfirm: () => {
            dispatch(setConfirmDefaultTipModal({ open: false }))
            onPay(null, true)
          },
          initialTipPercentage: defaultTipPercentage,
        }),
      )
      return
    }

    if (shouldNavigateToCharity()) {
      dispatch(setCFDScreen(CFD_SCREEN.CHARITY))
      return
    }

    handlePaymentClick()
  }

  // display a line item of the totals that were passed in
  const displayTotal = (title, amount, className, titleIcon) => {
    return (
      <Box pb={2} className={classes.total}>
        <Box className={classes.serviceChargeTotal}>
          <Typography
            style={{
              ...theme.custom.subtitle3,
              ...{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '275px', textOverflow: 'ellipsis', alignItems: 'center' },
            }}
            className={className ?? className}
          >
            {title}
          </Typography>
          {titleIcon}
        </Box>
        <Typography style={theme.custom.subtitle3} className={className ?? className}>
          {amount}
        </Typography>
      </Box>
    )
  }

  const displayTotals = () => {
    if (!cartTotals.isEmpty()) {
      const addPadding = () => {
        if (tips && tips.get('enabled')) {
          return 2
        } else {
          return 3
        }
      }

      const cartDiscount = cartTotals.get('discount')
      const cartTax = cartTotals.get('tax')
      const taxRulesBreakdown = cartTotals.get('taxRulesBreakdown')
      const useTaxClasses = cartTotals.get('useTaxClasses')
      const cartServiceCharge = cartTotals.get('serviceCharge')
      const serviceChargeLegal = cartTotals.get('serviceChargeLegal')
      const displayLegacyTax = cartTax !== 0 && useTaxClasses === 'false'
      const taxExempt = cartTotals.get('taxExempt') === 'true'

      return (
        <Box pt={addPadding()} className={classes.totalsContainer}>
          <Box pl={2} pr={2} className={cn(!tips?.get('enabled') || (tips?.get('enabled') !== 'true' && classes.totals))}>
            {displayTotal('Subtotal', centsToDollars(cartTotals.get('subtotal')))}
            {cartDiscount !== 0 && displayTotal('Discount', `${centsToDollars(cartDiscount)}`)}
            {cartServiceCharge !== 0 &&
              displayTotal(
                `${cartTotals.get('serviceChargeName')}`,
                centsToDollars(cartServiceCharge),
                null,
                serviceChargeLegal && (
                  <Info style={{ marginLeft: 10, color: 'white' }} onClick={() => setServiceChargeDialog(true)} />
                ),
              )}

            {useTaxClasses && (
              <>
                {Object.keys(taxRulesBreakdown).map((key) => {
                  const taxRule = taxRulesBreakdown[key]
                  return displayTotal(taxRule.taxRuleName, centsToDollars(taxRule.taxRuleAmountInCents))
                })}
              </>
            )}

            {displayLegacyTax && displayTotal('Tax', centsToDollars(cartTax))}
            {taxExempt && displayTotal('Tax', 'Exempt')}

            {tips?.get('enabled') === 'true' && displayTotal('Tip', centsToDollars(cartTotals.get('tip')))}
          </Box>
          <Box className={classes.payButton} pb={3}>
            <VNButton buttonVariant="button6" text={`Pay ${centsToDollars(parseInt(getTotalInCents()))}`} onClick={onPay} />
          </Box>
        </Box>
      )
    }
  }

  // tipping can be enabled / disabled per menu
  const displayTipping = () => {
    if (tips && tips.get('enabled') === 'true') {
      // only show the tip values if the total is over $0
      const totalInCents = getTotalWithoutTipInCents()

      let tipsDisplayArray = [[], []]
      let showTipSuggestions = false
      if (tips?.get('suggestions').length > 0) {
        showTipSuggestions = true
        tips.get('suggestions').forEach((tip) => {
          const tipValue = tip.tipPercentage ? parseFloat(tip.tipPercentage) : tip.tipDisplay

          // There is an issue on stadium that makes it so tipCustom have both tipCustom and tipFree set to true, this will look for that issue and fix it.
          if (tip.tipFree === 'true' && tip.tipCustom === 'false') {
            tipsDisplayArray[1][1] = {
              buttonVariant: 'button2',
              selected: tip.tipSelected === 'true' ? true : false,
              title: tip.tipDisplay,
              id: 'none',
            }
          } else if (tip.tipCustom === 'true') {
            tipsDisplayArray[1][0] = {
              buttonVariant: 'button2',
              selected: tip.tipSelected === 'true' ? true : false,
              title: 'Custom Tip',
              id: 'custom',
              disableRipple: true,
            }
          } else if (totalInCents > 0) {
            tipsDisplayArray[0].push({
              buttonVariant: 'button3',
              selected: tip.tipSelected === 'true' ? true : false,
              title: tip.tipDisplay,
              id: tipValue,
            })
          }
        })
      }

      return (
        <Box>
          <Typography className={classes.tipText} variant="h5">
            Add a Tip
          </Typography>
          {showTipSuggestions && (
            <VNButtonContainer
              buttonStyle={classes.containerButton}
              defaultSelected={false}
              onClick={onButtonContainerClick}
              buttons={tipsDisplayArray}
            />
          )}
        </Box>
      )
    }
  }

  if (!loaded) {
    return <VNProcessing />
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.list}>
        {displayTipping()}
        {displayTotals()}
      </Box>
      <VNDialog
        open={serviceChargeDialog}
        subtitleVariant={'h5'}
        subTitle={`${cartTotals.get('serviceChargeLegal')}`}
        save={'OKAY'}
        onSave={() => setServiceChargeDialog(false)}
        fullWidth
        height={460}
        width={624}
      />
    </Box>
  )
}
